import { Flex, Text, Skeleton } from '@mantine/core'
import { useEffect } from 'react'

import { useTranslation } from '@flecto/translations'
import { type CoreBookingProduct } from '@flecto/types'

import { useBookingProductAvailabilityCalculator } from '../../hooks/useBookingProductAvailabilityCalculator'

export type CellQuantityProps = {
  bookingProduct?: CoreBookingProduct
}

export function CellQuantity({ bookingProduct }: CellQuantityProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_items' })

  const {
    isLoading,
    isRefetching,
    isInfinite,
    infiniteSymbol,
    remainingQuantity,
    colorAvailability,
    setOriginalQuantity,
  } = useBookingProductAvailabilityCalculator({
    bookingId: bookingProduct?.booking?.internal_id,
    productId: bookingProduct?.product?.id,
    quantityStart: bookingProduct?.quantity,
  })

  useEffect(() => {
    // This is necessary to force recalcultate values on calculator
    setOriginalQuantity(bookingProduct?.quantity ?? 0)
  }, [bookingProduct])

  if (isLoading || isRefetching) {
    return (
      <Flex align='baseline' justify='center' direction='column' gap='sm'>
        <Skeleton h={15} w={20} radius='xl' />
        <Skeleton h={15} w={60} radius='xl' />
      </Flex>
    )
  }

  return (
    <Flex align='baseline' justify='center' direction='column'>
      <Text lh={1}>{bookingProduct?.quantity}</Text>
      <Text size='sm' c={colorAvailability}>
        {t(
          isInfinite || remainingQuantity === 1
            ? 'label_item_quantity_available'
            : 'label_item_quantity_available_plural',
          {
            replace: { count: isInfinite ? infiniteSymbol : remainingQuantity },
          }
        )}
      </Text>
    </Flex>
  )
}
